import { useToast } from '@datahub/design-system'

export interface LinkResponse {
  hasToken: boolean
  createdAt: string | null
  /** The live link, or null when it cannot be recovered. */
  url: string | null
  recoverable: boolean
}

/**
 * The reporting link this entity's contacts use, read straight off its profile.
 *
 * It is shown rather than hidden behind "Rotate link" because rotating is destructive:
 * it invalidates the link every contact already holds (LNK-04). An admin who only wants
 * to re-send the link needs to read it, not replace it.
 */
export function AccessLinkLine({ link, isLoading }: { link: LinkResponse | undefined; isLoading: boolean }) {
  const toast = useToast()
  const data = link
  const style = { fontSize: 12, lineHeight: '16px' } as const

  if (isLoading) {
    return <span className="font-mulish text-text-support" style={style}>Loading the link…</span>
  }

  if (!data?.hasToken) {
    return (
      <span className="font-mulish text-text-support" style={style}>
        No reporting link yet — “Create link” issues one.
      </span>
    )
  }

  if (!data.url) {
    return (
      <span className="font-mulish text-text-support" style={style}>
        This link predates token sealing, so it cannot be shown again. Rotate to issue a new one.
      </span>
    )
  }

  const copy = () => {
    void navigator.clipboard.writeText(data.url!)
    toast.success('Reporting link copied')
  }

  return (
    <span className="flex items-center" style={{ gap: 8, minWidth: 0 }}>
      <span className="font-mulish text-text-support flex-shrink-0" style={style}>Reporting link</span>
      <a
        href={data.url}
        target="_blank"
        rel="noreferrer"
        className="font-mono text-text-hyperlink hover:underline"
        style={{ ...style, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        title={data.url}
      >
        {data.url}
      </a>
      <button
        type="button"
        onClick={copy}
        className="font-mulish text-text-hyperlink hover:underline flex-shrink-0"
        style={{ ...style, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        Copy
      </button>
    </span>
  )
}
